import { useState, useMemo, useCallback } from 'react';
import { transactionsApi } from '../../../services/api';
import type { InvestmentProperty, FlowStep, InvestmentResult } from './InvestmentFlow.types';
import type { Transaction } from '../../../types';
import {
  MIN_INVESTMENT,
  calculateShares,
  calculateProjectedReturn,
} from './InvestmentFlow.utils';

export interface UseInvestmentFlowReturn {
  // ── State ──────────────────────────────────────────────────────────────────
  step: FlowStep;
  investmentAmount: number;
  selectedAccountId: string | null;
  isSubmitting: boolean;
  error: string | null;
  transactionResult: Transaction | null;
  // ── Derived (memoized) ────────────────────────────────────────────────────
  shares: number;
  projectedAnnualReturn: number;
  // ── Setters ───────────────────────────────────────────────────────────────
  setStep: (step: FlowStep) => void;
  setInvestmentAmount: (amount: number) => void;
  setSelectedAccountId: (id: string | null) => void;
  // ── Actions ───────────────────────────────────────────────────────────────
  canContinueFrom: (step: FlowStep) => boolean;
  submitInvestment: () => void;
  retrySubmit: () => void;
  goToPayment: () => void;
  buildResult: () => InvestmentResult | null;
}

export function useInvestmentFlow(
  property: InvestmentProperty,
  minInvestment: number = MIN_INVESTMENT
): UseInvestmentFlowReturn {
  const [step, setStep] = useState<FlowStep>('amount');
  const [investmentAmount, setInvestmentAmount] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionResult, setTransactionResult] = useState<Transaction | null>(null);

  const shares = useMemo(
    () => calculateShares(investmentAmount, property.pricePerShare),
    [investmentAmount, property.pricePerShare]
  );

  const projectedAnnualReturn = useMemo(
    () => calculateProjectedReturn(investmentAmount, property.annualYield),
    [investmentAmount, property.annualYield]
  );

  const canContinueFrom = useCallback(
    (fromStep: FlowStep): boolean => {
      switch (fromStep) {
        case 'amount':
          return investmentAmount >= minInvestment && shares > 0;
        case 'review':
          return true;
        case 'payment':
          return selectedAccountId !== null;
        default:
          return false;
      }
    },
    [investmentAmount, minInvestment, selectedAccountId, shares]
  );

  const doSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    setStep('processing');

    try {
      const response = await transactionsApi.buyShares({
        property_id: property.id,
        shares,
        price_per_share: property.pricePerShare,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message ?? response.error ?? 'Investment failed');
      }

      setTransactionResult(response.data);
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      // Stay on 'processing' — ProcessingStep renders error state
    } finally {
      setIsSubmitting(false);
    }
  }, [property.id, property.pricePerShare, shares]);

  const submitInvestment = useCallback(() => {
    void doSubmit();
  }, [doSubmit]);

  const retrySubmit = useCallback(() => {
    void doSubmit();
  }, [doSubmit]);

  const goToPayment = useCallback(() => {
    setError(null);
    setStep('payment');
  }, []);

  const buildResult = useCallback((): InvestmentResult | null => {
    if (!transactionResult) return null;
    return {
      transactionId: transactionResult.id,
      amount: transactionResult.total_amount,
      shares: transactionResult.shares,
      propertyId: transactionResult.property_id,
    };
  }, [transactionResult]);

  return {
    step,
    investmentAmount,
    selectedAccountId,
    isSubmitting,
    error,
    transactionResult,
    shares,
    projectedAnnualReturn,
    setStep,
    setInvestmentAmount,
    setSelectedAccountId,
    canContinueFrom,
    submitInvestment,
    retrySubmit,
    goToPayment,
    buildResult,
  };
}
